import { createSelector } from 'reselect';
import moment from 'utils/cops-moment';
import { referenceTypes } from '../../../utils/app-configs';

export const getIncludeSpecialDate = (data, flag, dateName, isLoading) => {
  const specialDate = data?.find(item => item.data && item.data[flag]);
  // only show special dates when they are included
  if (specialDate && !isLoading) {
    const specialDateObj = {
      date: moment(specialDate.date),
      specialDate: true,
      specialDateName: dateName,
    };
    return [...data, specialDateObj];
  }
  return data;
};

export const getTimeline = createSelector(
  [state => state.timeline.items || [], state => state.timeline.isLoading],
  (timeline, isLoading) => {
    let data = timeline;
    const today = timeline?.find(item => moment(item.date).isSame(moment(), 'day'));

    // only add today when we are done fetching the results
    // else the user will see an empty table for today
    if (!isLoading) {
      const newToday = {
        date: moment(),
        specialDate: true,
        specialDateName: 'TODAY',
      };
      // if today is present, make it a special day
      if (today) {
        newToday.specialDate = true;
        newToday.specialDateName = 'TODAY';
        newToday.data = today;
      }
      data = [...data, newToday];
    }

    data = getIncludeSpecialDate(data, 'flagAsEndOfWarranty', 'END_OF_WARRANTY', isLoading);
    data = getIncludeSpecialDate(data, 'flagAsEndOfContract', 'END_OF_CONTRACT', isLoading);
    data = getIncludeSpecialDate(data, 'flagAsInstallationDate', 'INSTALLATION_DATE', isLoading);

    // First, concat postal code and city; and order the items chronological
    const orderedTimeline = data
      ?.map(
        d =>
          d.data
            ? {
              ...d,
              installedat: [d.data.installedAtPostalCode, d.data.installedAtCity].join(' '),
            }
            : d,
      )
      ?.sort((a, b) => moment(b.date).diff(moment(a.date)));

    // Then, reduce the ordered timeline data to a format:
    // {
    //   '2017-06-01': [data],
    //   '2017-06-02': [data]
    // }
    return orderedTimeline.reduce((prev, curr) => {
      // the format needs to be the same as in short timeline segment-info segmentTimestamp
      const currentDate = moment(curr.date).format('YYYY-MM-DD');
      if (!prev[currentDate]) {
        prev[currentDate] = { data: [], specialDate: !!curr.specialDate };
      }
      if (!curr.id || curr.type === referenceTypes.equipment) {
        prev[currentDate] = {
          ...prev[currentDate],
          specialDate: curr.specialDate || prev[currentDate].specialDate,
          specialDateName: curr.specialDateName || prev[currentDate].specialDateName,
        };
      } else {
        prev[currentDate] = {
          ...prev[currentDate],
          data: [...prev[currentDate].data, curr],
        };
      }
      return prev;
    }, {});
  },
);

export const getStatusses = createSelector(
  [state => state.timeline.statusses || []],
  statusses =>
    ['all', ...statusses]?.map(status => ({
      text: `timeline.status.${status}`,
      value: status,
    })),
);

export const getFilters = createSelector(
  [state => state.timeline.filters || []],
  filters =>
    ['none', ...filters]?.map(filter => ({
      text: `timeline.status.${filter}`,
      value: filter,
    })),
);

export const getObjects = createSelector(
  [state => state.timeline.objects || []],
  objects =>
    ['all', ...objects]?.map(obj => ({
      text: `timeline.status.${obj}`,
      value: obj,
    })),
);

export const getTypes = createSelector(
  [state => state.timeline.types || []],
  types =>
    [{ id: 'all', name: 'All' }, ...types]?.map(type => ({
      text: type.name,
      value: type.id,
    })),
);

export default getTimeline;
